import React, { useState, useEffect } from 'react';
import {
    Box,
    Paper,
    Typography,
    Button,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination,
    IconButton,
    Chip,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Grid,
    Card,
    CardContent,
    Alert,
    CircularProgress,
    Tooltip,
} from '@mui/material';
import {
    Add as AddIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
    Visibility as ViewIcon,
    Search as SearchIcon,
    Clear as ClearIcon,
    FilterList as FilterIcon,
} from '@mui/icons-material';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../../store/store';
import {
    getVehicles,
    getBranches,
    deleteVehicle,
    setFilters,
    clearFilters,
    Vehicle,
    VehicleFilters,
} from '../../store/slices/vehicleSlice';
import VehicleForm from './VehicleForm';
import VehicleView from './VehicleView';
import ConfirmationDialog from '../../components/ConfirmationDialog';
import GenericButton from '../../components/GenericButton';

const Vehicles: React.FC = () => {
    const dispatch = useDispatch<AppDispatch>();
    const {
        vehicles,
        branches,
        pagination,
        loading,
        error,
        filters,
    } = useSelector((state: RootState) => state.vehicles);

    // Local state
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(20);
    const [searchQuery, setSearchQuery] = useState('');
    const [showFilters, setShowFilters] = useState(false);
    const [showForm, setShowForm] = useState(false);
    const [showView, setShowView] = useState(false);
    const [showDeleteDialog, setShowDeleteDialog] = useState(false);
    const [selectedVehicle, setSelectedVehicle] = useState<Vehicle | null>(null);
    const [deleteVehicleId, setDeleteVehicleId] = useState<number | null>(null);

    // Load data on component mount and when dependencies change
    useEffect(() => {
        dispatch(getVehicles({ page: page + 1, per_page: rowsPerPage, filters }));
    }, [dispatch, page, rowsPerPage, filters]);

    useEffect(() => {
        dispatch(getBranches());
    }, [dispatch]);

    const handlePageChange = (event: unknown, newPage: number) => {
        setPage(newPage);
    };

    const handleRowsPerPageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const handleSearch = () => {
        dispatch(setFilters({ query: searchQuery }));
        setPage(0);
    };

    const handleClearSearch = () => {
        setSearchQuery('');
        dispatch(setFilters({ query: undefined }));
        setPage(0);
    };

    const handleFilterChange = (filterType: keyof VehicleFilters, value: any) => {
        dispatch(setFilters({ [filterType]: value }));
        setPage(0);
    };

    const handleClearFilters = () => {
        dispatch(clearFilters());
        setSearchQuery('');
        setPage(0);
    };

    const handleAddVehicle = () => {
        setSelectedVehicle(null);
        setShowForm(true);
    };

    const handleEditVehicle = (vehicle: Vehicle) => {
        setSelectedVehicle(vehicle);
        setShowForm(true);
    };

    const handleViewVehicle = (vehicle: Vehicle) => {
        setSelectedVehicle(vehicle);
        setShowView(true);
    };

    const handleDeleteVehicle = (vehicle: Vehicle) => {
        setDeleteVehicleId(vehicle.id);
        setShowDeleteDialog(true);
    };

    const confirmDelete = async () => {
        if (deleteVehicleId) {
            await dispatch(deleteVehicle(deleteVehicleId));
            setShowDeleteDialog(false);
            setDeleteVehicleId(null);
        }
    };

    const handleFormClose = () => {
        setShowForm(false);
        setSelectedVehicle(null);
    };

    const handleViewClose = () => {
        setShowView(false);
        setSelectedVehicle(null);
    };

    const getStatusChip = (vehicle: Vehicle) => {
        // For now, we'll show a default status since vehicles don't have a status field yet
        return <Chip label="Active" color="success" size="small" />;
    };

    const formatDate = (dateString: string) => {
        return new Date(dateString).toLocaleDateString();
    };

    return (
        <Box sx={{ p: 3 }}>
            {/* Header */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h4" component="h1" gutterBottom>
                    Vehicles
                </Typography>
                <GenericButton
                    variant="primary"
                    startIcon={<AddIcon />}
                    onClick={handleAddVehicle}
                    sx={{ minWidth: 120 }}
                >
                    Add Vehicle
                </GenericButton>
            </Box>

            {/* Error Alert */}
            {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                    {error}
                </Alert>
            )}

            {/* Search and Filters */}
            <Card sx={{ mb: 3 }}>
                <CardContent>
                    <Grid container spacing={2} alignItems="center">
                        <Grid size={{ xs: 12, md: 4 }}>
                            <TextField
                                fullWidth
                                label="Search vehicles"
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                                InputProps={{
                                    endAdornment: (
                                        <Box>
                                            {searchQuery && (
                                                <IconButton size="small" onClick={handleClearSearch}>
                                                    <ClearIcon />
                                                </IconButton>
                                            )}
                                            <IconButton size="small" onClick={handleSearch}>
                                                <SearchIcon />
                                            </IconButton>
                                        </Box>
                                    ),
                                }}
                            />
                        </Grid>
                        <Grid size={{ xs: 12, md: 2 }}>
                            <Button
                                variant="outlined"
                                startIcon={<FilterIcon />}
                                onClick={() => setShowFilters(!showFilters)}
                                fullWidth
                            >
                                Filters
                            </Button>
                        </Grid>
                        <Grid size={{ xs: 12, md: 2 }}>
                            <Button
                                variant="outlined"
                                onClick={handleClearFilters}
                                fullWidth
                            >
                                Clear All
                            </Button>
                        </Grid>
                    </Grid>

                    {/* Filter Options */}
                    {showFilters && (
                        <Box sx={{ mt: 2, pt: 2, borderTop: 1, borderColor: 'divider' }}>
                            <Grid container spacing={2}>
                                <Grid size={{ xs: 12, md: 3 }}>
                                    <FormControl fullWidth size="small">
                                        <InputLabel>Branch</InputLabel>
                                        <Select
                                            value={filters.branch_id || ''}
                                            onChange={(e) => handleFilterChange('branch_id', e.target.value || undefined)}
                                            label="Branch"
                                        >
                                            <MenuItem value="">All Branches</MenuItem>
                                            {branches.map((branch) => (
                                                <MenuItem key={branch.id} value={branch.id}>
                                                    {branch.name}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Grid>
                                <Grid size={{ xs: 12, md: 3 }}>
                                    <FormControl fullWidth size="small">
                                        <InputLabel>Vehicle Type</InputLabel>
                                        <Select
                                            value={filters.type_of_vehicle || ''}
                                            onChange={(e) => handleFilterChange('type_of_vehicle', e.target.value || undefined)}
                                            label="Vehicle Type"
                                        >
                                            <MenuItem value="">All Types</MenuItem>
                                            <MenuItem value="4 Wheeler">4 Wheeler</MenuItem>
                                            <MenuItem value="2 Wheeler">2 Wheeler</MenuItem>
                                            <MenuItem value="Truck">Truck</MenuItem>
                                            <MenuItem value="Van">Van</MenuItem>
                                        </Select>
                                    </FormControl>
                                </Grid>
                                <Grid size={{ xs: 12, md: 3 }}>
                                    <FormControl fullWidth size="small">
                                        <InputLabel>Ownership</InputLabel>
                                        <Select
                                            value={filters.ownership || ''}
                                            onChange={(e) => handleFilterChange('ownership', e.target.value || undefined)}
                                            label="Ownership"
                                        >
                                            <MenuItem value="">All</MenuItem>
                                            <MenuItem value="Company">Company</MenuItem>
                                            <MenuItem value="Rented">Rented</MenuItem>
                                            <MenuItem value="Private">Private</MenuItem>
                                        </Select>
                                    </FormControl>
                                </Grid>
                            </Grid>
                        </Box>
                    )}
                </CardContent>
            </Card>

            {/* Vehicles Table */}
            <Paper sx={{ width: '100%', overflow: 'hidden' }}>
                <TableContainer>
                    <Table stickyHeader>
                        <TableHead>
                            <TableRow>
                                <TableCell sx={{ fontWeight: 600 }}>Vehicle Number</TableCell>
                                <TableCell sx={{ fontWeight: 600 }}>Type</TableCell>
                                <TableCell sx={{ fontWeight: 600 }}>Make & Model</TableCell>
                                <TableCell sx={{ fontWeight: 600 }}>Branch</TableCell>
                                <TableCell sx={{ fontWeight: 600 }}>Ownership</TableCell>
                                <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                                <TableCell sx={{ fontWeight: 600 }}>Created</TableCell>
                                <TableCell sx={{ fontWeight: 600 }} align="center">Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {loading ? (
                                <TableRow>
                                    <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                                        <CircularProgress />
                                    </TableCell>
                                </TableRow>
                            ) : vehicles.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                                        <Typography variant="body2" color="text.secondary">
                                            No vehicles found
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            ) : (
                                vehicles.map((vehicle) => (
                                    <TableRow key={vehicle.id} hover>
                                        <TableCell>
                                            <Typography variant="body2" fontWeight={500}>
                                                {vehicle.vehicle_number}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2">
                                                {vehicle.type_of_vehicle || '-'}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2">
                                                {vehicle.make && vehicle.model
                                                    ? `${vehicle.make} ${vehicle.model}`
                                                    : vehicle.make || vehicle.model || '-'
                                                }
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2">
                                                {vehicle.branch?.name || '-'}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2">
                                                {vehicle.ownership || '-'}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            {getStatusChip(vehicle)}
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2">
                                                {formatDate(vehicle.created_at)}
                                            </Typography>
                                        </TableCell>
                                        <TableCell align="center">
                                            <Tooltip title="View Details">
                                                <IconButton
                                                    size="small"
                                                    onClick={() => handleViewVehicle(vehicle)}
                                                    color="primary"
                                                >
                                                    <ViewIcon />
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip title="Edit Vehicle">
                                                <IconButton
                                                    size="small"
                                                    onClick={() => handleEditVehicle(vehicle)}
                                                    color="primary"
                                                >
                                                    <EditIcon />
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip title="Delete Vehicle">
                                                <IconButton
                                                    size="small"
                                                    onClick={() => handleDeleteVehicle(vehicle)}
                                                    color="error"
                                                >
                                                    <DeleteIcon />
                                                </IconButton>
                                            </Tooltip>
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>

                {/* Pagination */}
                {pagination && (
                    <TablePagination
                        rowsPerPageOptions={[10, 20, 50]}
                        component="div"
                        count={pagination.total}
                        rowsPerPage={rowsPerPage}
                        page={page}
                        onPageChange={handlePageChange}
                        onRowsPerPageChange={handleRowsPerPageChange}
                        labelDisplayedRows={({ from, to, count }) =>
                            `${from}-${to} of ${count !== -1 ? count : `more than ${to}`}`
                        }
                    />
                )}
            </Paper>

            {/* Vehicle Form Modal */}
            <VehicleForm
                open={showForm}
                vehicle={selectedVehicle}
                onClose={handleFormClose}
                onSuccess={() => {
                    handleFormClose();
                    dispatch(getVehicles({ page: page + 1, per_page: rowsPerPage, filters }));
                }}
            />

            {/* Vehicle View Modal */}
            {selectedVehicle && (
                <VehicleView
                    open={showView}
                    vehicle={selectedVehicle}
                    onClose={handleViewClose}
                />
            )}

            {/* Delete Confirmation Dialog */}
            <ConfirmationDialog
                open={showDeleteDialog}
                title="Delete Vehicle"
                message="Are you sure you want to delete this vehicle? This action cannot be undone."
                onConfirm={confirmDelete}
                onCancel={() => setShowDeleteDialog(false)}
            />
        </Box>
    );
};

export default Vehicles;
